import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { FiSearch, FiEdit3, FiTrash2, FiPrinter } from "react-icons/fi";
import InfiniteScroll from "react-infinite-scroll-component";
import DatePicker from "../../../../../components/Input/DatePicker";
import Select from "react-select";
import LoadInfiniteScroll from "../../../../../components/LoadInfiniteScroll";
import { Loading } from "../../../../../components/Loading";
import { BrComerceApiService } from "../../../../../services/BrComerceApiService";
import store from "../../../../../store/store";
import { capitalizeFirstLetter } from "../../../../../utils/CapitalizeFirstLetter";
import debounce from "../../../../../utils/debounce";
import ScrollDiv from "../../../components/ScrollDiv";
import styles from "./styles.module.scss";
import InputSelectAsync, {
    selectStyle
} from "../../../../../components/InputSelectAsync";
import { MdClear } from "react-icons/md";
import Container from "../../../components/Container";
import { StyledTooltip } from "../../../components/Tooltip";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { formatOwnCollectStartAndEnd } from "../../../../../utils/OwnCollectTime";
import { OwnCollectStatusEnum } from "../../../../../enums/OwnCollectStatusEnum";

const collectStatusArray = Object.values(OwnCollectStatusEnum).map(status => ({
    value: status.id,
    label: status.message
}));

collectStatusArray.unshift({
    value: undefined,
    label: "Todas",
})

const ListCollect = () => {
    const api = new BrComerceApiService();

    const { auth } = store.getState();
    const { user } = auth;
    const history = useHistory();

    const today = new Date();
    const initialDate = moment(today).subtract(7, "days").toDate();
    const finalDate = moment(today).toDate();

    const [loading, setLoading] = useState(false);
    const [collects, setCollects] = useState([]);

    const [search, setSearch] = useState("");
    const [actualPage, setActualPage] = useState(1);
    const [loadingInfinit, setLoadingInfinit] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [periodInitialDate, setPeriodInitialDate] = useState(initialDate);
    const [periodFinalDate, setPeriodFinalDate] = useState(finalDate);
    const [searchClient, setSearchClient] = useState(null);

    const [selectedStatusInFilter, setSelectedStatusInFilter] = useState({
        value: "",
        label: "Todos"
    });

    const handleChangeSelectFilterByStatus = e => {
        setSelectedStatusInFilter(e);
    };

    const handleFetchData = useCallback(async () => {
        setLoadingInfinit(true);
        try {
            const relations = '["user"]';
            const startDate = moment(periodInitialDate).format("YYYY-MM-DD");
            const endDate = moment(periodFinalDate).format("YYYY-MM-DD");
            const response = await api.makeHttpRequest({
                method: "GET",
                url: `/own-collect`,
                params: {
                    perPage: 20,
                    page: actualPage + 1,
                    relations,
                    ...(searchClient?.sub && { subUser: searchClient?.sub }),
                    ...(selectedStatusInFilter && { status: selectedStatusInFilter.value }),
                    ...(periodInitialDate && { startDate }),
                    ...(periodFinalDate && { endDate }),
                    ...(search && { id: search }),
                }
            });

            setCollects(prev => [...prev, ...response]);
            setActualPage(actualPage + 1);

            if (response?.length === 20) {
                setHasMore(true);
            } else {
                setHasMore(false);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoadingInfinit(false);
            setLoading(false);
        }
    }, [
        actualPage,
        api,
        periodInitialDate,
        selectedStatusInFilter,
        periodFinalDate,
        search,
        searchClient,
    ]);

    const getCollects = useCallback(
        debounce(async () => {
            setLoading(true);

            try {
                const relations = '["user"]';
                const startDate = moment(periodInitialDate).format("YYYY-MM-DD");
                const endDate = moment(periodFinalDate).format("YYYY-MM-DD");
                const response = await api.makeHttpRequest({
                    method: "GET",
                    url: `/own-collect`,
                    params: {
                        perPage: 20,
                        page: 1,
                        relations,
                        ...(searchClient?.sub && { subUser: searchClient?.sub }),
                        ...(selectedStatusInFilter && { status: selectedStatusInFilter.value }),
                        ...(periodInitialDate && { startDate }),
                        ...(periodFinalDate && { endDate }),
                        ...(search && { id: search }),
                    }
                });

                setCollects(response);
                setActualPage(1);

                if (response?.length === 20) {
                    setHasMore(true);
                } else {
                    setHasMore(false);
                }

                setLoading(false);
            } catch (e) {
                setLoading(false);
                console.error(e);
            }
        }, 500),
        [
            user,
            searchClient,
            periodInitialDate,
            selectedStatusInFilter,
            periodFinalDate,
            search,
            actualPage,
            api,
        ]
    );

    const handleOnChangeCalendarPeriod = e => {
        if (e.dateEnd) {
            const dateStart = moment(e.dateStart, "DD/MM/YYYY");
            const dateEnd = moment(e.dateEnd, "DD/MM/YYYY");

            setPeriodInitialDate(dateStart);
            setPeriodFinalDate(dateEnd);
        }
    };

    const handleClearPeriodFilter = () => {
        setPeriodInitialDate("");
        setPeriodFinalDate("");
    };

    const handleEditCollect = (id, canEdit) => {
        if (!canEdit) {
            return history.push(`/coletas/${id}/detalhes`);
        }
        history.push(`/coletas/${id}/editar`);
    };

    const handleRemoveCollect = id => {
        Swal.fire({
            title: "Deseja deletar a coleta?",
            text: "Esta ação não poderá ser revertida.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Deletar",
            cancelButtonText: "Cancelar"
        }).then(async (res) => {
            try {
                if (res?.value) {
                    setLoading(true)
                    await api.makeHttpRequest({
                        method: "DELETE",
                        url: `/own-collect/${id}`,
                    });

                    Swal.fire({
                        title: "Sucesso!",
                        text: "A coleta foi deletada com sucesso!",
                        icon: "success",
                        confirmButtonText: "Fechar",
                    }).then(() => {
                        getCollects();
                    })
                }
            } catch (error) {
                Swal.fire({
                    title: "Erro!",
                    text: "Não foi possível deletar a coleta!",
                    icon: "error"
                });
                setLoading(false)
            } finally {
                setLoading(false)
            }
        });
    };

    const getCollectStatusById = useCallback(id => {
        const status = Object.entries(OwnCollectStatusEnum).find(
            st => st[1].id === id
        );

        return status;
    }, [OwnCollectStatusEnum]);

    const handlePrintCollects = async id => {
        try {
            setLoading(true);
            const relations = '["user", "collectAddress"]';

            const ownCollect = await api.makeHttpRequest({
                method: "GET",
                url: `/own-collect/${id}`,
                params: {
                    relations,
                }
            });

            // if (ownCollect && (idInvoicesStatus === 1 || idInvoicesStatus === 5)) { // TODO: verificar questão do status
            if (ownCollect) {
                window.localStorage.setItem("Brcom:EtiquetaColeta", JSON.stringify(id));
                window.open("/etiqueta/coleta", "_blank");
            } else {
                return Swal.fire({
                    title: "Ops!",
                    text: "Etiquetas indisponíveis",
                    icon: "warning"
                });
            }
        } catch (err) {
            return Swal.fire({
                title: "Erro!",
                text: "Erro ao buscar coleta",
                icon: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        window.setPageTitle("Coletas");
    }, []);

    useEffect(() => {
        getCollects();
    }, [
        search,
        periodInitialDate,
        periodFinalDate,
        searchClient,
        selectedStatusInFilter
    ]);

    return (
        <>
            <Loading loading={loading} />
            <Container title="Coletas">
                <div className={styles.contentBody}>
                    <div className={styles.filterers}>
                        <div className={`${styles.inputContainer}`}>
                            <div className={styles.input}>
                                <input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar por código da coleta"
                                    value={search}
                                    onChange={e => setSearch(e.currentTarget.value)}
                                />
                                {!search.length && <FiSearch size={20} color="#409e6a" />}

                                {!!search?.length && (
                                    <MdClear
                                        style={{ cursor: "pointer" }}
                                        onClick={() => setSearch("")}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={styles.inputSelectAsync}>
                            <InputSelectAsync
                                search={searchClient}
                                setSearch={setSearchClient}
                            />
                        </div>

                        <div
                            className={styles.inputCalendar}
                        >
                            <div className={styles.input}>
                                <DatePicker
                                    className={"datePickerConfig"}
                                    change={handleOnChangeCalendarPeriod}
                                    placeholder="Filtrar por período"
                                    onClearFields={handleClearPeriodFilter}
                                    dateStart={periodInitialDate}
                                    dateFinal={periodFinalDate}
                                    range
                                />
                            </div>
                        </div>
                        <div className={styles.formSelect}>
                            <Select
                                onChange={handleChangeSelectFilterByStatus}
                                options={collectStatusArray}
                                value={
                                    selectedStatusInFilter ||
                                    collectStatusArray.find(item => item.value === undefined)
                                }
                                placeholder="Status"
                                styles={selectStyle}
                            />
                        </div>
                    </div>
                    <ScrollDiv data={collects}>
                        <InfiniteScroll
                            dataLength={collects?.length}
                            next={handleFetchData}
                            hasMore={hasMore}
                            loader={loadingInfinit && <LoadInfiniteScroll />}
                            scrollableTarget="scrollDiv"
                            className={styles.infiniteScroll}
                        >
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead className={styles.tableHead}>
                                        <tr>
                                            <th>Coleta</th>
                                            <th>Cliente</th>
                                            <th>Data Criação</th>
                                            <th>Horário da Coleta</th>
                                            <th>Status</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className={styles.tableBody}>
                                        {collects?.map(collect => {
                                            const idStatus = collect?.status;

                                            const status = getCollectStatusById(idStatus)
                                            const canEditCollect = idStatus === OwnCollectStatusEnum.A_FAZER.id;
                                            return (
                                                <tr key={collect.id}>
                                                    <td className={styles.thCode}>
                                                        <a
                                                            href={`detalhes-coleta-propria/${collect.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {collect.id}
                                                        </a>
                                                    </td>
                                                    <td className={styles.thClient}>
                                                        <p>
                                                            {capitalizeFirstLetter(collect?.user?.userData[1].Value) ||
                                                                "--"}
                                                        </p>
                                                    </td>
                                                    <td className={styles.thCreated}>
                                                        <p>{moment(collect?.created_at)
                                                            .subtract(3, "hours")
                                                            .format("DD/MM/YYYY")} </p>
                                                    </td>
                                                    <td>
                                                        {formatOwnCollectStartAndEnd(collect?.collectTimeStart, collect?.collectTimeEnd) || "--"}
                                                    </td>
                                                    <td>
                                                        {status?.length ? status[1]?.message : "--"}
                                                    </td>
                                                    <td className={styles.tooltipWrapper}>
                                                        {!canEditCollect && <StyledTooltip text="Detalhes" position="top" arrow>
                                                            <div className={styles.actionIcon}>
                                                                <FiSearch
                                                                    size={20}
                                                                    color="#409e6a"
                                                                    onClick={() => history.push(`/detalhes-coleta-propria/${collect?.id}`)}
                                                                />
                                                            </div>
                                                        </StyledTooltip>}
                                                        {!!canEditCollect && <StyledTooltip text="Editar" position="top" arrow>
                                                            <div className={styles.actionIcon}>
                                                                <FiEdit3
                                                                    size={20}
                                                                    color="#409e6a"
                                                                    onClick={() => handleEditCollect(collect?.id, canEditCollect)}
                                                                />
                                                            </div>
                                                        </StyledTooltip>}
                                                        {!!canEditCollect && <StyledTooltip text="Remover" position="top" arrow>
                                                            <div className={styles.actionIcon}>
                                                                <FiTrash2
                                                                    size={20}
                                                                    color="#409e6a"
                                                                    onClick={() => handleRemoveCollect(collect?.id)}
                                                                />
                                                            </div>
                                                        </StyledTooltip>}
                                                        {!!canEditCollect && <StyledTooltip text="Imprimir Etiqueta" position="top" arrow>
                                                            <div className={styles.actionIcon}>
                                                                <FiPrinter
                                                                    size={20}
                                                                    color="#409E6A"
                                                                    onClick={() => handlePrintCollects(collect?.id)}
                                                                />
                                                            </div>
                                                        </StyledTooltip>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </InfiniteScroll>
                    </ScrollDiv>
                </div>
            </Container>
        </>
    );
};

export { ListCollect };
